import { CalendarOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, DatePicker, Form, FormInstance, Input, Modal, Select, Tag } from "antd";
import zhCN from "antd/lib/date-picker/locale/zh_CN";
import { FC, KeyboardEvent } from "react";
import TaskSubtaskManager, {
  TaskSubtaskManagerProps,
} from "../TaskSubtaskManager";
import "./index.css";

export interface TaskEditorValues {
  title: string;
  due_time?: any;
  priority?: "low" | "medium" | "high" | "urgent";
  status?: "todo" | "doing" | "blocked" | "review" | "done" | "discarded";
}

interface TaskEditorModalProps {
  mode: "create" | "edit";
  open: boolean;
  form: FormInstance<TaskEditorValues>;
  saving: boolean;
  readOnly?: boolean;
  tags: string[];
  tagInput: string;
  statusOptions: Array<{ label: string; value: string; disabled?: boolean }>;
  subtaskEditing: boolean;
  subtaskManagerProps: TaskSubtaskManagerProps;
  onCancel: () => void;
  onFinish: (values: TaskEditorValues) => void | Promise<void>;
  onTagInputChange: (value: string) => void;
  onAddTag: () => void;
  onRemoveTag: (tag: string) => void;
}

const PRIORITY_OPTIONS = [
  { label: "低", value: "low" },
  { label: "中", value: "medium" },
  { label: "高", value: "high" },
  { label: "紧急", value: "urgent" },
];

const TaskEditorModal: FC<TaskEditorModalProps> = ({
  mode,
  open,
  form,
  saving,
  readOnly = false,
  tags,
  tagInput,
  statusOptions,
  subtaskEditing,
  subtaskManagerProps,
  onCancel,
  onFinish,
  onTagInputChange,
  onAddTag,
  onRemoveTag,
}) => {
  const isCreate = mode === "create";
  const handleTagEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    onAddTag();
  };

  return (
    <Modal
      maskClosable={false}
      keyboard={false}
      centered
      className="taskEditorModal"
      destroyOnClose
      footer={null}
      onCancel={onCancel}
      open={open}
      title={isCreate ? "新建任务节点" : "编辑任务节点"}
      width={1380}
    >
      <div className="taskEditorModal__workspace">
        <section className="taskEditorModal__main">
          <header className="taskEditorModal__sectionHeader">
            <span>{isCreate ? <PlusOutlined /> : <EditOutlined />}</span>
            <div>
              <strong>主任务信息</strong>
              <small>{isCreate ? "新任务" : "任务属性"}</small>
            </div>
          </header>

          <Form<TaskEditorValues>
            className="projectForm taskEditorModal__form"
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
            <Form.Item
              label="节点标题"
              name="title"
              rules={[{ required: true, message: "请输入任务节点标题" }]}
            >
              <Input disabled={readOnly} placeholder="请输入任务节点标题" />
            </Form.Item>

            <Form.Item label="标签">
              <Input
                disabled={readOnly}
                onChange={(event) => onTagInputChange(event.target.value)}
                onPressEnter={handleTagEnter}
                placeholder="输入标签后回车"
                value={tagInput}
              />
              <div className="taskEditorModal__tags">
                {tags.length ? (
                  tags.map((tag) => (
                    <Tag
                      closable={!readOnly}
                      key={tag}
                      onClose={(event) => {
                        event.preventDefault();
                        onRemoveTag(tag);
                      }}
                    >
                      {tag}
                    </Tag>
                  ))
                ) : (
                  <span>暂无标签</span>
                )}
              </div>
            </Form.Item>

            <div className="taskEditorModal__fieldGrid">
              <Form.Item label="优先级" name="priority">
                <Select
                  disabled={readOnly}
                  options={PRIORITY_OPTIONS}
                  popupClassName="taskEditorModal__selectDropdown"
                />
              </Form.Item>
              <Form.Item label="状态" name="status">
                <Select
                  options={statusOptions}
                  popupClassName="taskEditorModal__selectDropdown"
                />
              </Form.Item>
            </div>

            <Form.Item
              label="预期完结时间"
              name="due_time"
              rules={[{ required: true, message: "请选择预期完结时间" }]}
            >
              <DatePicker
                disabled={readOnly}
                format="YYYY年MM月DD日"
                locale={zhCN}
                placeholder="请选择预期完结时间"
                popupClassName="taskEditorModal__dateDropdown"
                suffixIcon={<CalendarOutlined />}
                style={{ width: "100%" }}
              />
            </Form.Item>

            <div className="taskEditorModal__footer">
              <span className={subtaskEditing || readOnly ? "is-visible" : ""}>
                {readOnly
                  ? "该任务已废弃，仅可切换状态进行恢复"
                  : "请先保存或取消当前子任务"}
              </span>
              <div>
                <Button onClick={onCancel}>取消</Button>
                <Button
                  disabled={subtaskEditing}
                  htmlType="submit"
                  loading={saving}
                  type="primary"
                >
                  {isCreate ? "创建任务" : "保存修改"}
                </Button>
              </div>
            </div>
          </Form>
        </section>

        <aside className="taskEditorModal__subtasks">
          <TaskSubtaskManager {...subtaskManagerProps} />
        </aside>
      </div>
    </Modal>
  );
};

export default TaskEditorModal;
